/**
 * Composant carte de session
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Affiche les informations d'une session avec options de révocation.
 */

  // Client Component pour Next.js

import type { Session } from '../../../application/dto/dtos';

/**
 * Props du composant SessionCard
 */
export interface SessionCardProps {
  /** Données de la session */
  session: Session;
  /** Indique si c'est la session actuelle */
  isCurrent?: boolean;
  /** Callback de révocation */
  onRevoke?: (sessionId: string) => void;
  /** État de chargement */
  isRevoking?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant SessionCard
 *
 * @example
 * ```tsx
 * function SessionsList() {
 *   const { sessions, currentSessionId, revokeSession } = useSession();
 *
 *   return (
 *     <div>
 *       {sessions.map((session) => (
 *         <SessionCard
 *           key={session.id}
 *           session={session}
 *           isCurrent={session.id === currentSessionId}
 *           onRevoke={revokeSession}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function SessionCard({
  session,
  isCurrent = false,
  onRevoke,
  isRevoking = false,
  className = '',
}: SessionCardProps) {
  /**
   * Extraire les informations de l'appareil depuis userAgent
   */
  const getDeviceInfo = (): { appareil: string; navigateur: string } => {
    const ua = session.userAgent || 'Unknown';

    // Détection de l'appareil
    let appareil = 'Desktop';
    if (/mobile/i.test(ua)) {
      if (/android/i.test(ua)) appareil = 'Android Mobile';
      else if (/iphone/i.test(ua)) appareil = 'iPhone';
      else appareil = 'Mobile';
    } else if (/tablet|ipad/i.test(ua)) {
      appareil = /ipad/i.test(ua) ? 'iPad' : 'Tablet';
    }

    // Détection du navigateur
    let navigateur = 'Unknown';
    if (/chrome/i.test(ua) && !/edg/i.test(ua)) navigateur = 'Chrome';
    else if (/firefox/i.test(ua)) navigateur = 'Firefox';
    else if (/safari/i.test(ua) && !/chrome/i.test(ua)) navigateur = 'Safari';
    else if (/edg/i.test(ua)) navigateur = 'Edge';

    return { appareil, navigateur };
  };

  /**
   * Formater la date de dernière activité
   */
  const formatLastActivity = (date: Date | string): string => {
    const activityDate = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - activityDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;

    return activityDate.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: activityDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  /**
   * Obtenir l'icône de l'appareil
   */
  const getDeviceIcon = (appareil: string): JSX.Element => {
    const deviceLower = appareil.toLowerCase();

    if (deviceLower.includes('mobile') || deviceLower.includes('android') || deviceLower.includes('iphone')) {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    }

    if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    }

    // Desktop par défaut
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    );
  };

  /**
   * Gérer la révocation
   */
  const handleRevoke = () => {
    if (!isCurrent && onRevoke) {
      onRevoke(session.id);
    }
  };

  return (
    <div
      className={`bg-surface rounded-lg border ${
        isCurrent
          ? 'border-primary ring-2 ring-primary/20'
          : 'border-border'
      } p-4 ${className}`}
    >
      <div className="flex items-start justify-between">
        {/* Informations session */}
        <div className="flex items-start space-x-3 flex-1">
          {/* Icône appareil */}
          <div
            className={`flex-shrink-0 p-2 rounded-lg ${
              isCurrent
                ? 'bg-accent-light text-accent'
                : 'bg-surface-overlay text-content-muted'
            }`}
          >
            {getDeviceIcon(getDeviceInfo().appareil)}
          </div>

          {/* Détails */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-content truncate">
                {getDeviceInfo().appareil}
              </h4>
              {isCurrent && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent-light text-accent">
                  Actuelle
                </span>
              )}
            </div>

            {/* Navigateur */}
            {getDeviceInfo().navigateur !== 'Unknown' && (
              <p className="text-xs text-content-muted mb-1">
                {getDeviceInfo().navigateur}
              </p>
            )}

            {/* Localisation et IP */}
            <div className="flex items-center gap-2 text-xs text-content-subtle">
              {/* Localisation non disponible dans le modèle actuel */}
              {session.adresseIP && (
                <span className="flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {session.adresseIP}
                </span>
              )}
            </div>

            {/* Dernière activité */}
            {session.dernierAcces && (
              <p className="mt-2 text-xs text-content-subtle">
                Dernière activité : {formatLastActivity(session.dernierAcces)}
              </p>
            )}

            {/* Date de création */}
            <p className="text-xs text-content-subtle">
              Créée le{' '}
              {new Date(session.dateCreation).toLocaleString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>

        {/* Bouton révocation */}
        {!isCurrent && (
          <button
            type="button"
            onClick={handleRevoke}
            disabled={isRevoking}
            className="ml-3 flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-danger rounded-md text-xs font-medium text-danger bg-surface hover:bg-danger-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Révoquer cette session"
          >
            {isRevoking ? (
              <>
                <svg
                  className="animate-spin -ml-0.5 mr-1.5 h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Révocation...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Révoquer
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
